import { useCallback, useEffect, useRef } from 'react';

import { getPartialRecognition, sendPartialRecognition } from 'services/dashboard.service';
import { findPlayer } from 'services/profile.service';
import { User } from 'types/user';

import { Recognition, isBehaviorKey } from './recognition';
import { isCoinKey, isValidPoints } from './recognition-points';
import { STEP, Step } from './recognition-steps';

/** Avisa a Home para reler se há reconhecimento em preenchimento. */
const DRAFT_CHANGED_EVENT = 'reload:partialRecognition';

/** Espera curta antes de gravar: escolhas em sequência viram uma gravação só. */
const SAVE_DELAY_MS = 50;

/** As escolhas feitas até aqui e a etapa em que a pessoa parou. */
export type RecognitionDraft = Partial<Recognition> & { step: Step };

export function notifyDraftChanged() {
  document.dispatchEvent(new CustomEvent(DRAFT_CHANGED_EVENT));
}

function savedStep(value: unknown): Step {
  const isStep = typeof value === 'number' && Object.values(STEP).includes(value as Step);

  return isStep ? (value as Step) : STEP.colleague;
}

async function findSavedColleague(id: unknown): Promise<User | undefined> {
  if (!id) return undefined;

  const players = await findPlayer(id as string, true);

  return players?.length ? players[0] : undefined;
}

type SavedChoices = { hasTarget: boolean; hasBehavior: boolean; hasPoints: boolean };

/**
 * Sem o colega não há o que continuar; sem um comportamento conhecido ou sem um valor válido não
 * há o que revisar nem enviar. A tela abre na primeira etapa a que falta uma escolha.
 */
function firstStepStillOpen(step: Step, { hasTarget, hasBehavior, hasPoints }: SavedChoices): Step {
  if (!hasTarget) return STEP.colleague;
  if (!hasBehavior) return Math.min(step, STEP.behavior) as Step;

  return step > STEP.coinPoints && !hasPoints ? STEP.coinPoints : step;
}

/**
 * Lê o rascunho gravado e devolve só o que ainda vale: o comportamento e o tipo precisam existir,
 * o valor precisa caber na faixa do tipo, e a etapa recua até onde falta uma escolha (REC-53).
 * Sem dado nenhum, ou em caso de falha, devolve `null` e a tela abre vazia.
 */
export async function readRecognitionDraft(): Promise<RecognitionDraft | null> {
  try {
    const saved = await getPartialRecognition();
    const step = savedStep(saved?.step);

    if (!saved || !(saved.target || saved.behavior || saved.coin || saved.message || step > STEP.colleague)) return null;

    const target = await findSavedColleague(saved.target);
    const behavior = isBehaviorKey(saved.behavior) ? saved.behavior : undefined;
    const coin = isCoinKey(saved.coin) ? saved.coin : undefined;
    const points = coin && isValidPoints(coin, saved.points) ? (saved.points as number) : undefined;

    return {
      target,
      behavior,
      coin,
      points,
      message: saved.message || undefined,
      step: firstStepStillOpen(step, { hasTarget: !!target, hasBehavior: !!behavior, hasPoints: points !== undefined }),
    };
  } catch (error) {
    console.error('Erro ao carregar reconhecimento parcial:', error);

    return null;
  }
}

/** O que vai para `bb_recognition_partial__c`: só os campos preenchidos. */
function draftPayload(userId: string, draft: RecognitionDraft) {
  const payload: Record<string, unknown> = { _id: userId, step: draft.step };

  if (draft.target?._id) payload.target = draft.target._id;
  if (draft.behavior) payload.behavior = draft.behavior;
  if (draft.coin) payload.coin = draft.coin;
  if (draft.points) payload.points = draft.points;
  if (draft.message) payload.message = draft.message;

  return payload;
}

/** Gravação e limpeza do rascunho do reconhecimento (REC-54, REC-55). */
export function useRecognitionDraft(userId?: string) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => cancelPendingSave, [cancelPendingSave]);

  const saveSoon = useCallback(
    (draft: RecognitionDraft) => {
      if (!userId) return;

      cancelPendingSave();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        sendPartialRecognition(draftPayload(userId, draft));
      }, SAVE_DELAY_MS);
    },
    [userId, cancelPendingSave],
  );

  const clear = useCallback(async () => {
    cancelPendingSave();
    // Gravar só o `_id` apaga o rascunho.
    await sendPartialRecognition(userId ? { _id: userId } : null);
    notifyDraftChanged();
  }, [userId, cancelPendingSave]);

  return { saveSoon, clear };
}
